import {
  ImageUrlFormats,
  useImageUrlsQuery,
  usePrivateFileImageUrls,
  usePublicFileImageUrls,
} from '@marine/services/src/rest-api';
import React, { FC, useRef } from 'react';
import ImagePlaceholder, { PlaceHolderAsBase64 } from './placeholder';

type Props = React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> & {
  /**
   * A file's **uuid**, served off the Laravel `GET /files/{uuid}` route with a Clerk token.
   *
   * The way to address any file inside the account: a boat's cover, a trip's, a checklist's, a
   * thumbnail in the file browser. Every local row is keyed by its uuid from the moment it exists,
   * so this works for a file that has not finished syncing — which `imageId` never could.
   */
  fileUuid?: string;
  /**
   * A file's uuid on the credential-free `GET /files/public/{uuid}` route.
   *
   * For images a signed-out visitor is allowed to see, which in practice means the public checklist
   * library: the same covers the marketing sites render. Pass this or `fileUuid`, not both — they
   * are the same file on two routes, and the difference is whether a session is required.
   */
  publicImageUuid?: string;
  /**
   * A file's numeric `laravel_id`, served signed off the old `mdcApiHost` service.
   *
   * @deprecated Use `fileUuid`. This addresses the **Lumen** file service, which is being retired
   * along with the rest of Lumen — its route is declared `[0-9]+`, so it can only ever take the
   * numeric id. It stays only for the legacy GraphQL-backed screens in `@marine/files` and
   * `@marine/boats`, whose queries read `File.id` and which go when Lumen does. Nothing new should
   * use it: a file that has not synced has no `laravel_id`, which is why the pickers had to disable
   * rows and wait on it.
   */
  imageId?: string;
  format?: keyof ImageUrlFormats;
};

/**
 * One file, at whichever rendition was asked for.
 *
 * Three ways in, and they are three different services rather than three spellings of one. The two
 * uuid props are the Laravel file service, public and private; `imageId` is the Lumen service that
 * is on its way out. Giving one the other's identifier is why public covers used to render as
 * placeholders, so a caller passes exactly one.
 */
export const Image: FC<Props> = ({ src, fileUuid, publicImageUuid, imageId, format, ...props }) => {
  // All three run every render, as the rules of hooks require. Each does nothing without an
  // identifier, and a caller has given only one of them.
  const publicUrls = usePublicFileImageUrls(publicImageUuid);
  const privateUrls = usePrivateFileImageUrls(publicImageUuid ? undefined : fileUuid);
  const legacyUrls = useImageUrlsQuery(publicImageUuid || fileUuid ? undefined : imageId);

  const isError = useRef<boolean>(false);

  const handleOnError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (!isError.current) {
      isError.current = true;
      (e.target as HTMLImageElement).onerror = null;
      (e.target as HTMLImageElement).src = PlaceHolderAsBase64;
    }
  };

  const result = publicImageUuid ? publicUrls : fileUuid ? privateUrls : legacyUrls;
  const imageSrc = result[format || 'base'];

  if (imageSrc) {
    return (
      <img
        src={imageSrc}
        {...props}
        loading="lazy"
        onError={handleOnError}
        style={{ backgroundColor: '#F1F5F9', backgroundSize: 'contain' }}
      />
    );
  }

  if (src) {
    return (
      <img
        src={src}
        {...props}
        loading="lazy"
        onError={handleOnError}
        style={{ backgroundColor: '#F1F5F9', backgroundSize: 'contain' }}
      />
    );
  }

  return <ImagePlaceholder style={{ backgroundColor: '#F1F5F9', backgroundSize: 'contain', width: '100%' }} />;
};
